/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Magazine {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import { Section, CmsImage } from "@bw/bits"
import { MagazineActive as MagazineModule, Teasers } from "@bw/modules"
// parse is used to ensure that HTML sent by the CMS is converted to React components
import parse from "html-react-parser"

const Magazines = ({
  title,
  subtitle,
  button,
  buttonPosition,
  magazine,
  displayArchives,
  anchor,
}) => {
  const {
    letsHearFromYou,
    subscribeToMagazine,
    allCollectionPages,
    magazines,
  } = useStaticQuery(graphql`
    query MagazineModuleQuery {
      letsHearFromYou: globals(
        handle: { eq: "pages" }
        letsHearFromYou: { ne: null }
      ) {
        id
        letsHearFromYou
      }
      subscribeToMagazine: globals(
        handle: { eq: "pages" }
        subscribeToMagazine: { ne: null }
      ) {
        id
        subscribeToMagazine
      }
      allCollectionPages {
        nodes {
          statamicId
          title
          slug
        }
      }
      magazines: allCollectionMagazines(sort: { publishDate: DESC }) {
        nodes {
          statamicId
          title
          slug
          teaserImage: cover {
            localImage {
              childImageSharp {
                gatsbyImageData(width: 300)
              }
            }
          }
        }
      }
    }
  `)

  let parole = undefined

  if (letsHearFromYou.letsHearFromYou.length > 0) {
    parole = allCollectionPages.nodes.find(
      node => node.statamicId === letsHearFromYou.letsHearFromYou[0]
    )
  }

  let register = undefined

  if (subscribeToMagazine.subscribeToMagazine.length > 0) {
    register = allCollectionPages.nodes.find(
      node => node.statamicId === subscribeToMagazine.subscribeToMagazine[0]
    )
  }

  const filteredMagazines = magazines.nodes.filter(
    node => node.statamicId !== magazine.id
  )

  return (
    <>
      <Section {...{ anchor }}>
        <MagazineModule
          title={title}
          excerpt={subtitle ? parse(subtitle) : null}
          button={button}
          buttonPosition={buttonPosition?.value}
          subscribeUrl={register?.slug}
          pdfUrl={magazine.slug ? magazine.slug + "/pdf" : null}
          paroleUrl={parole?.slug}
          image={<CmsImage src={magazine?.cover} alt={magazine?.title} />}
        />
      </Section>
      {displayArchives && filteredMagazines.length > 0 && (
        <Section>
          <Teasers numberOfColumns={4}>
            {filteredMagazines.map((item, i) => (
              <Link key={i} to={item.slug}>
                {item.teaserImage?.localImage && (
                  <CmsImage
                    src={item.teaserImage || "/placeholder-teaser.jpg"}
                    alt={item.title}
                  />
                )}
              </Link>
            ))}
          </Teasers>
        </Section>
      )}
    </>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on MagazineModule {
 *   ...MagazineModuleFragment
 * }
 *
 * This is also what you will get in the props of your Magazine
 * resolver component.
 */
export const query = graphql`
  fragment MagazinesModuleFragment on MagazinesModule {
    displayArchives
    magazine {
      id
      title
      slug
      cover {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 330, quality: 99)
          }
        }
      }
    }
  }
`

export { Magazines }
